import { useState, useEffect, useCallback } from 'react';
import { Link } from 'react-router-dom';
import { FaChartBar, FaCheckCircle, FaTimesCircle, FaClock, FaFileAlt, FaExclamationTriangle, FaEye, FaTimes, FaCopy, FaFileInvoice, FaUsers, FaFilePdf } from 'react-icons/fa';
import { getFranchiseeRegistrationReport, getJobsWithoutInvoicesReport, downloadComplianceRegisterPDF } from '../services/reportService';

const ReportsPage = () => {
  // Tab state
  const [activeTab, setActiveTab] = useState('franchisee-registration');

  // Franchisee Registration Report State
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [reportData, setReportData] = useState([]);
  const [stats, setStats] = useState({
    total: 0,
    registered: 0,
    notRegistered: 0,
    completeCompliance: 0,
    partialCompliance: 0,
    noCompliance: 0
  });
  const [filterStatus, setFilterStatus] = useState('all'); // all, registered, not-registered
  const [filterCompliance, setFilterCompliance] = useState('all'); // all, complete, partial, none
  const [selectedFranchisee, setSelectedFranchisee] = useState(null); // For modal
  const [copySuccess, setCopySuccess] = useState(false); // For copy feedback
  const [showDelimiterModal, setShowDelimiterModal] = useState(false); // For delimiter selection modal

  // Jobs Without Invoices Report State
  const [jobsReportLoading, setJobsReportLoading] = useState(false);
  const [jobsReportError, setJobsReportError] = useState(null);
  const [jobsReportData, setJobsReportData] = useState([]);
  const [jobsReportSummary, setJobsReportSummary] = useState({
    totalCompleted: 0,
    totalMissing: 0,
    missingInvoice: 0,
    missingRcti: 0,
    missingBoth: 0
  });
  const [jobsFilterMissingType, setJobsFilterMissingType] = useState('any'); // any, invoice, rcti, both
  const [jobsFilterStartDate, setJobsFilterStartDate] = useState('');
  const [jobsFilterEndDate, setJobsFilterEndDate] = useState('');
  const [jobsReportFetched, setJobsReportFetched] = useState(false);

  // Compliance Register PDF state
  const [pdfDownloading, setPdfDownloading] = useState(false);
  const [pdfError, setPdfError] = useState(null);
  const [pdfSuccess, setPdfSuccess] = useState(false);

  const calculateStats = (data, summary = null) => {
    const registrationSummary = summary?.registration || null;
    const complianceSummary = summary?.compliance || null;

    const total = registrationSummary?.totalAllowedEmails ?? data.length;
    const registered = registrationSummary?.registered ?? data.filter(f => f.registrationStatus === 'registered').length;
    const notRegistered = registrationSummary?.notRegistered ?? data.filter(f => f.registrationStatus === 'new').length;
    const completeCompliance = complianceSummary?.fullyCompliant ?? data.filter(f => f.complianceStatus?.isComplete).length;
    const partialCompliance = complianceSummary?.partiallyCompliant ?? data.filter(f => f.complianceStatus?.hasAny && !f.complianceStatus?.isComplete).length;
    const noCompliance = complianceSummary?.noComplianceUploads ?? data.filter(f => !f.complianceStatus?.hasAny).length;

    setStats({
      total,
      registered,
      notRegistered,
      completeCompliance,
      partialCompliance,
      noCompliance
    });
  };

  const fetchReport = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const result = await getFranchiseeRegistrationReport();
      const data = Array.isArray(result) ? result : (result?.data || []);
      const summary = Array.isArray(result) ? null : (result?.summary || null);
      setReportData(data);
      calculateStats(data, summary);
    } catch (err) {
      setError(err.message || 'Failed to load registration report');
      console.error('Error fetching registration report:', err);
    } finally {
      setLoading(false);
    }
  }, []);

  const fetchJobsWithoutInvoicesReport = useCallback(async () => {
    setJobsReportLoading(true);
    setJobsReportError(null);
    try {
      const options = {};
      if (jobsFilterStartDate) options.startDate = jobsFilterStartDate;
      if (jobsFilterEndDate) options.endDate = jobsFilterEndDate;
      if (jobsFilterMissingType && jobsFilterMissingType !== 'any') options.missingType = jobsFilterMissingType;

      const result = await getJobsWithoutInvoicesReport(options);
      setJobsReportData(result.data);
      setJobsReportSummary(result.summary);
      setJobsReportFetched(true);
    } catch (err) {
      setJobsReportError(err.message || 'Failed to load jobs without invoices report');
      console.error('Error fetching jobs without invoices report:', err);
    } finally {
      setJobsReportLoading(false);
    }
  }, [jobsFilterStartDate, jobsFilterEndDate, jobsFilterMissingType]);

  const handleJobsReportRefresh = () => {
    setJobsReportFetched(false);
    fetchJobsWithoutInvoicesReport();
  };

  const handleDownloadCompliancePDF = async () => {
    setPdfDownloading(true);
    setPdfError(null);
    setPdfSuccess(false);
    try {
      await downloadComplianceRegisterPDF();
      setPdfSuccess(true);
      setTimeout(() => setPdfSuccess(false), 4000);
    } catch (err) {
      setPdfError(err.message || 'Failed to download PDF');
      console.error('Error downloading compliance register PDF:', err);
    } finally {
      setPdfDownloading(false);
    }
  };

  useEffect(() => {
    if (activeTab === 'franchisee-registration') {
      fetchReport();
    } else if (activeTab === 'jobs-without-invoices' && !jobsReportFetched) {
      fetchJobsWithoutInvoicesReport();
    }
  }, [activeTab, jobsReportFetched, fetchReport, fetchJobsWithoutInvoicesReport]);

  const getFilteredData = () => {
    let filtered = [...reportData];

    // Filter by registration status
    if (filterStatus === 'registered') {
      filtered = filtered.filter(f => f.registrationStatus === 'registered');
    } else if (filterStatus === 'not-registered') {
      filtered = filtered.filter(f => f.registrationStatus === 'new');
    }

    // Filter by compliance status
    if (filterCompliance === 'complete') {
      filtered = filtered.filter(f => f.complianceStatus?.isComplete);
    } else if (filterCompliance === 'partial') {
      filtered = filtered.filter(f => f.complianceStatus?.hasAny && !f.complianceStatus?.isComplete);
    } else if (filterCompliance === 'none') {
      filtered = filtered.filter(f => !f.complianceStatus?.hasAny);
    }

    return filtered;
  };

  const getStatusBadge = (status) => {
    if (status === 'registered') {
      return (
        <span className="inline-flex items-center px-3 py-1 rounded-full text-sm font-medium bg-green-100 text-green-800">
          <FaCheckCircle className="mr-1" />
          Registered
        </span>
      );
    } else {
      return (
        <span className="inline-flex items-center px-3 py-1 rounded-full text-sm font-medium bg-yellow-100 text-yellow-800">
          <FaClock className="mr-1" />
          Not Registered
        </span>
      );
    }
  };

  const getComplianceBadge = (complianceStatus) => {
    if (!complianceStatus || !complianceStatus.hasAny) {
      return (
        <span className="inline-flex items-center px-3 py-1 rounded-full text-sm font-medium bg-red-100 text-red-800">
          <FaTimesCircle className="mr-1" />
          None (0/4)
        </span>
      );
    } else if (complianceStatus.isComplete) {
      return (
        <span className="inline-flex items-center px-3 py-1 rounded-full text-sm font-medium bg-green-100 text-green-800">
          <FaCheckCircle className="mr-1" />
          Complete (4/4)
        </span>
      );
    } else {
      return (
        <span className="inline-flex items-center px-3 py-1 rounded-full text-sm font-medium bg-yellow-100 text-yellow-800">
          <FaExclamationTriangle className="mr-1" />
          Partial ({complianceStatus.completedCount}/{complianceStatus.totalRequired})
        </span>
      );
    }
  };

  const handleCopyEmails = () => {
    setShowDelimiterModal(true);
  };

  const copyWithDelimiter = async (delimiter) => {
    setShowDelimiterModal(false);
    const filteredData = getFilteredData();
    const emailList = filteredData.map(item => item.email).join(delimiter);
    try {
      await navigator.clipboard.writeText(emailList);
      setCopySuccess(true);
      setTimeout(() => setCopySuccess(false), 3000);
    } catch (err) {
      console.error('Failed to copy emails:', err);
      setError('Failed to copy emails to clipboard');
    }
  };

  const filteredData = getFilteredData();

  // Filter jobs report data based on local filter (missingType is applied on fetch, this is for client-side additional filtering)
  const getFilteredJobsData = () => {
    return jobsReportData;
  };

  const filteredJobsData = getFilteredJobsData();

  // Get status badge for jobs without invoices report
  const getJobStatusBadge = (job) => {
    if (job.isCarwash) {
      return (
        <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-blue-100 text-blue-800">
          Car Wash
        </span>
      );
    } else if (job.allocationType === 'monthly') {
      return (
        <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-yellow-100 text-yellow-800">
          Monthly Allocation
        </span>
      );
    } else {
      return (
        <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-red-100 text-red-800">
          Generation Failed
        </span>
      );
    }
  };

  const getMissingBadge = (job) => {
    if (!job.hasInvoice && !job.hasRcti) {
      return (
        <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-red-100 text-red-800">
          Both Missing
        </span>
      );
    } else if (!job.hasInvoice) {
      return (
        <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-orange-100 text-orange-800">
          Invoice Missing
        </span>
      );
    } else {
      return (
        <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-yellow-100 text-yellow-800">
          RCTI Missing
        </span>
      );
    }
  };

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12 font-sans">
      {/* Header */}
      <div className="flex justify-between items-center mb-2">
        <h1 className="text-3xl font-extrabold text-gray-900 tracking-tight sm:text-4xl">
          Reports
        </h1>
        <Link
          to="/"
          className="text-gray-500 hover:text-gray-900 font-medium transition-colors"
        >
          Back to Dashboard
        </Link>
      </div>

      {/* Report Tabs */}
      <div className="bg-white shadow-sm ring-1 ring-gray-900/5 rounded-2xl overflow-hidden mb-6 mt-8">
        <div className="border-b border-gray-200">
          <nav className="flex -mb-px">
            <button
              onClick={() => setActiveTab('franchisee-registration')}
              className={`flex items-center px-6 py-4 text-sm font-medium border-b-2 transition duration-200 ${
                activeTab === 'franchisee-registration'
                  ? 'border-green-600 text-gray-900'
                  : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
              }`}
            >
              <FaUsers className="mr-2" />
              Franchisee Registration
            </button>
            <button
              onClick={() => setActiveTab('jobs-without-invoices')}
              className={`flex items-center px-6 py-4 text-sm font-medium border-b-2 transition duration-200 ${
                activeTab === 'jobs-without-invoices'
                  ? 'border-green-600 text-gray-900'
                  : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
              }`}
            >
              <FaFileInvoice className="mr-2" />
              Jobs Without Invoices/RCTIs
            </button>
          </nav>
        </div>
      </div>

      {activeTab === 'franchisee-registration' && !loading && (
        <div className="sticky top-4 z-20 mb-6">
          <div className="bg-white/95 backdrop-blur rounded-2xl shadow-sm ring-1 ring-gray-900/5 border border-gray-100 p-4 flex flex-col md:flex-row md:items-center md:justify-between gap-3">
            <div>
              <p className="text-sm font-semibold text-gray-800">Report Actions</p>
              <p className="text-xs text-gray-600">Export the compliance register PDF without leaving this report.</p>
            </div>
            <div className="flex flex-wrap gap-2">
              <button
                onClick={fetchReport}
                disabled={loading || pdfDownloading}
                className="inline-flex items-center px-4 py-2 bg-white border border-gray-300 text-gray-700 text-sm font-semibold rounded-full shadow-sm hover:bg-gray-50 transition duration-300 disabled:opacity-50"
              >
                Refresh Data
              </button>
              <button
                onClick={handleDownloadCompliancePDF}
                disabled={pdfDownloading}
                className="inline-flex items-center px-4 py-2 bg-green-600 text-white text-sm font-semibold rounded-full shadow-sm hover:bg-green-500 transition duration-300 disabled:opacity-50"
              >
                {pdfDownloading ? (
                  <>
                    <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                    Generating PDF...
                  </>
                ) : (
                  <>
                    <FaFilePdf className="mr-2" />
                    Export Compliance PDF
                  </>
                )}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Error Messages */}
      {error && activeTab === 'franchisee-registration' && (
        <div className="bg-red-100 border-l-4 border-red-500 text-red-700 p-4 mb-6 rounded">
          <p className="font-bold">Error</p>
          <p>{error}</p>
        </div>
      )}
      {jobsReportError && activeTab === 'jobs-without-invoices' && (
        <div className="bg-red-100 border-l-4 border-red-500 text-red-700 p-4 mb-6 rounded">
          <p className="font-bold">Error</p>
          <p>{jobsReportError}</p>
        </div>
      )}
      {pdfError && activeTab === 'franchisee-registration' && (
        <div className="bg-red-100 border-l-4 border-red-500 text-red-700 p-4 mb-6 rounded">
          <p className="font-bold">PDF Export Error</p>
          <p>{pdfError}</p>
        </div>
      )}

      {/* Success Message for Copy */}
      {copySuccess && activeTab === 'franchisee-registration' && (
        <div className="bg-green-100 border-l-4 border-green-500 text-green-700 p-4 mb-6 rounded">
          <p className="font-bold">Success</p>
          <p>Emails copied to clipboard! ({filteredData.length} email{filteredData.length !== 1 ? 's' : ''})</p>
        </div>
      )}
      {pdfSuccess && activeTab === 'franchisee-registration' && (
        <div className="bg-green-100 border-l-4 border-green-500 text-green-700 p-4 mb-6 rounded">
          <p className="font-bold">Download Started</p>
          <p>The Compliance Register PDF has been downloaded to your device.</p>
        </div>
      )}

      {/* === JOBS WITHOUT INVOICES REPORT === */}
      {activeTab === 'jobs-without-invoices' && (
        <>
          {/* Report Title */}
          <div className="bg-white shadow-sm ring-1 ring-gray-900/5 rounded-2xl p-6 mb-6">
            <h2 className="text-2xl font-bold text-gray-800 mb-4">
              Completed Jobs Without Invoices/RCTIs Report
            </h2>
            <p className="text-gray-600">
              This report shows completed jobs that are missing invoices and/or RCTIs. This can happen due to application errors during generation, monthly allocation jobs pending month end, or car wash jobs that are consolidated monthly.
            </p>
          </div>

          {/* Statistics Cards */}
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-4 mb-6">
            <div className="bg-white shadow-sm ring-1 ring-gray-900/5 rounded-2xl p-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-gray-500 text-sm font-medium">Total Completed</p>
                  <p className="text-3xl font-bold text-gray-800">{jobsReportSummary.totalCompleted}</p>
                </div>
                <div className="bg-gray-100 rounded-full p-3">
                  <FaChartBar className="text-gray-600 text-2xl" />
                </div>
              </div>
            </div>

            <div className="bg-white shadow-sm ring-1 ring-gray-900/5 rounded-2xl p-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-gray-500 text-sm font-medium">Missing Invoice</p>
                  <p className="text-3xl font-bold text-orange-600">{jobsReportSummary.missingInvoice}</p>
                </div>
                <div className="bg-orange-100 rounded-full p-3">
                  <FaFileInvoice className="text-orange-600 text-2xl" />
                </div>
              </div>
            </div>

            <div className="bg-white shadow-sm ring-1 ring-gray-900/5 rounded-2xl p-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-gray-500 text-sm font-medium">Missing RCTI</p>
                  <p className="text-3xl font-bold text-yellow-600">{jobsReportSummary.missingRcti}</p>
                </div>
                <div className="bg-yellow-100 rounded-full p-3">
                  <FaFileAlt className="text-yellow-600 text-2xl" />
                </div>
              </div>
            </div>

            <div className="bg-white shadow-sm ring-1 ring-gray-900/5 rounded-2xl p-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-gray-500 text-sm font-medium">Missing Both</p>
                  <p className="text-3xl font-bold text-red-600">{jobsReportSummary.missingBoth}</p>
                </div>
                <div className="bg-red-100 rounded-full p-3">
                  <FaTimesCircle className="text-red-600 text-2xl" />
                </div>
              </div>
            </div>

            <div className="bg-white shadow-sm ring-1 ring-gray-900/5 rounded-2xl p-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-gray-500 text-sm font-medium">Total Missing</p>
                  <p className="text-3xl font-bold text-green-600">{jobsReportSummary.totalMissing}</p>
                </div>
                <div className="bg-green-600 bg-opacity-20 rounded-full p-3">
                  <FaExclamationTriangle className="text-green-600 text-2xl" />
                </div>
              </div>
            </div>
          </div>

          {/* Filters */}
          <div className="bg-white shadow-sm ring-1 ring-gray-900/5 rounded-2xl p-6 mb-6">
            <div className="flex justify-between items-center mb-4">
              <h3 className="text-lg font-semibold text-gray-800">Filters</h3>
              <button
                onClick={handleJobsReportRefresh}
                disabled={jobsReportLoading}
                className="inline-flex items-center bg-green-600 hover:bg-green-500 text-white font-semibold py-2 px-5 rounded-full shadow-sm transition duration-300 disabled:opacity-50"
              >
                {jobsReportLoading ? (
                  <>
                    <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                    Loading...
                  </>
                ) : (
                  'Refresh Report'
                )}
              </button>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Missing Type
                </label>
                <select
                  value={jobsFilterMissingType}
                  onChange={(e) => setJobsFilterMissingType(e.target.value)}
                  className="w-full px-4 py-2 border border-gray-300 rounded-md focus:ring-green-500 focus:border-green-600"
                >
                  <option value="any">Any (Invoice or RCTI)</option>
                  <option value="invoice">Invoice Only</option>
                  <option value="rcti">RCTI Only</option>
                  <option value="both">Both Missing</option>
                </select>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Start Date (Completion)
                </label>
                <input
                  type="date"
                  value={jobsFilterStartDate}
                  onChange={(e) => setJobsFilterStartDate(e.target.value)}
                  className="w-full px-4 py-2 border border-gray-300 rounded-md focus:ring-green-500 focus:border-green-600"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  End Date (Completion)
                </label>
                <input
                  type="date"
                  value={jobsFilterEndDate}
                  onChange={(e) => setJobsFilterEndDate(e.target.value)}
                  className="w-full px-4 py-2 border border-gray-300 rounded-md focus:ring-green-500 focus:border-green-600"
                />
              </div>
            </div>
          </div>

          {/* Report Table */}
          {jobsReportLoading ? (
            <div className="bg-white rounded-lg shadow-md p-8">
              <div className="flex justify-center items-center">
                <div className="text-center">
                  <div className="animate-spin rounded-full h-16 w-16 border-b-2 border-green-600 mx-auto mb-4"></div>
                  <p className="text-gray-600">Loading report...</p>
                </div>
              </div>
            </div>
          ) : (
            <div className="bg-white shadow-sm ring-1 ring-gray-900/5 rounded-2xl overflow-hidden">
              <table className="w-full divide-y divide-gray-200 table-fixed">
                <thead className="bg-gray-800">
                  <tr>
                    <th className="px-2 py-3 text-left text-xs font-medium text-white uppercase tracking-wider">
                      Job 
                    </th>
                    <th className="px-2 py-3 text-left text-xs font-medium text-white uppercase tracking-wider">
                      Franchisee
                    </th>
                    <th className="px-2 py-3 text-left text-xs font-medium text-white uppercase tracking-wider">
                      Client
                    </th>
                    <th className="px-2 py-3 text-left text-xs font-medium text-white uppercase tracking-wider">
                      Supplier
                    </th>
                    <th className="px-2 py-3 text-left text-xs font-medium text-white uppercase tracking-wider">
                      Completion Date
                    </th>
                    <th className="px-2 py-3 text-left text-xs font-medium text-white uppercase tracking-wider">
                      Service Type
                    </th>
                    <th className="px-2 py-3 text-left text-xs font-medium text-white uppercase tracking-wider">
                      Invoice Price
                    </th>
                    <th className="px-2 py-3 text-left text-xs font-medium text-white uppercase tracking-wider">
                      Status
                    </th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {filteredJobsData.length === 0 ? (
                    <tr>
                      <td colSpan="8" className="px-6 py-8 text-center text-gray-500">
                        {jobsReportFetched ? 'No jobs found matching the criteria.' : 'Click "Refresh Report" to load data.'}
                      </td>
                    </tr>
                  ) : (
                    filteredJobsData.map((job, index) => (
                      <tr key={job.jobId || index} className="hover:bg-gray-50">
                        <td className="px-2 py-3 text-sm text-gray-900">
                          <Link
                            to={`/jobs-list/edit/${job.jobId}`}
                            className="text-green-600 hover:text-gray-900 hover:underline font-medium"
                          >
                            <p className="truncate break-words">{job.jobId}</p>
                          </Link>
                          <div className="text-xs text-gray-500">
                            {job.jobDate ? `Job Date: ` + new Date(job.jobDate).toLocaleDateString() : '-'}
                          </div>
                        </td>
                        <td className="px-2 py-3 text-sm text-gray-900 break-words">
                          <div>{job.franchiseeFirstName} {job.franchiseeLastName}</div>
                          <div className="text-xs text-gray-500">{job.franchiseeFseCode || ''}</div>
                        </td>
                        <td className="px-2 py-3 text-sm text-gray-900 break-words">
                          {job.client || '-'}
                        </td>
                        <td className="px-2 py-3 text-sm text-gray-900 break-words">
                          {job.supplier || '-'}
                        </td>
                        <td className="px-2 py-3 text-sm text-gray-600">
                          {job.completionDate ? new Date(job.completionDate).toLocaleDateString() : '-'}
                        </td>
                        <td className="px-2 py-3 text-sm text-gray-600 break-words">
                          {job.serviceType || '-'}
                        </td>
                        <td className="px-2 py-3 text-sm text-gray-900">
                          {job.invoicePrice != null ? `$${job.invoicePrice.toFixed(2)}` : '-'}
                        </td>
                        <td className="px-2 py-3 text-sm">
                          {getMissingBadge(job)}
                        </td>
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            </div>
          )}

          {/* Total Results */}
          <div className="mt-4 text-sm text-gray-600">
            Showing {filteredJobsData.length} of {jobsReportSummary.totalMissing} jobs with missing invoice/RCTI
          </div>
        </>
      )}

      {/* === FRANCHISEE REGISTRATION REPORT === */}
      {activeTab === 'franchisee-registration' && (
        <>
          {/* Loading State */}
          {loading ? (
            <div className="bg-white shadow-sm ring-1 ring-gray-900/5 rounded-2xl p-8">
              <div className="flex justify-center items-center">
                <div className="text-center">
                  <div className="animate-spin rounded-full h-16 w-16 border-b-2 border-green-600 mx-auto mb-4"></div>
                  <p className="text-gray-600">Loading report...</p>
                </div>
              </div>
            </div>
          ) : (
            <>
              {/* Report Title */}
              <div className="bg-white shadow-sm ring-1 ring-gray-900/5 rounded-2xl p-6 mb-6">
                <h2 className="text-2xl font-bold text-gray-800 mb-4">
                  Franchisee Registration & Compliance Status Report
                </h2>
                <p className="text-gray-600">
                  This report shows the registration status of all allowed franchisee emails and their compliance document upload status.
                </p>
              </div>

      {/* Statistics Cards */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-6 gap-4 mb-6">
        <div className="bg-white shadow-sm ring-1 ring-gray-900/5 rounded-2xl p-6">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-gray-500 text-sm font-medium">Total Emails</p>
              <p className="text-3xl font-bold text-gray-800">{stats.total}</p>
            </div>
            <div className="bg-gray-100 rounded-full p-3">
              <FaChartBar className="text-gray-600 text-2xl" />
            </div>
          </div>
        </div>

        <div className="bg-white shadow-sm ring-1 ring-gray-900/5 rounded-2xl p-6">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-gray-500 text-sm font-medium">Registered</p>
              <p className="text-3xl font-bold text-green-600">{stats.registered}</p>
            </div>
            <div className="bg-green-100 rounded-full p-3">
              <FaCheckCircle className="text-green-600 text-2xl" />
            </div>
          </div>
        </div>

        <div className="bg-white shadow-sm ring-1 ring-gray-900/5 rounded-2xl p-6">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-gray-500 text-sm font-medium">Not Registered</p>
              <p className="text-3xl font-bold text-yellow-600">{stats.notRegistered}</p>
            </div>
            <div className="bg-yellow-100 rounded-full p-3">
              <FaClock className="text-yellow-600 text-2xl" />
            </div>
          </div>
        </div>

        <div className="bg-white shadow-sm ring-1 ring-gray-900/5 rounded-2xl p-6">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-gray-500 text-sm font-medium">Complete</p>
              <p className="text-3xl font-bold text-green-600">{stats.completeCompliance}</p>
            </div>
            <div className="bg-green-100 rounded-full p-3">
              <FaCheckCircle className="text-green-600 text-2xl" />
            </div>
          </div>
        </div>

        <div className="bg-white shadow-sm ring-1 ring-gray-900/5 rounded-2xl p-6">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-gray-500 text-sm font-medium">Partial</p>
              <p className="text-3xl font-bold text-yellow-600">{stats.partialCompliance}</p>
            </div>
            <div className="bg-yellow-100 rounded-full p-3">
              <FaExclamationTriangle className="text-yellow-600 text-2xl" />
            </div>
          </div>
        </div>

        <div className="bg-white shadow-sm ring-1 ring-gray-900/5 rounded-2xl p-6">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-gray-500 text-sm font-medium">None</p>
              <p className="text-3xl font-bold text-red-600">{stats.noCompliance}</p>
            </div>
            <div className="bg-red-100 rounded-full p-3">
              <FaTimesCircle className="text-red-600 text-2xl" />
            </div>
          </div>
        </div>
      </div>
      <div className="mb-6 text-xs text-gray-500">
        Compliance totals are calculated from active franchisee records (same source used by Compliance PDF export).
      </div>

      {/* Filters */}
      <div className="bg-white shadow-sm ring-1 ring-gray-900/5 rounded-2xl p-6 mb-6">
        <div className="flex justify-between items-center mb-4">
          <h3 className="text-lg font-semibold text-gray-800">Filters</h3>
          <button
            onClick={handleCopyEmails}
            className="inline-flex items-center bg-green-600 hover:bg-green-500 text-white font-semibold py-2 px-5 rounded-full shadow-sm transition duration-300"
          >
            <FaCopy className="mr-2" />
            {copySuccess ? 'Copied!' : 'Copy All Emails'}
          </button>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Registration Status
            </label>
            <select
              value={filterStatus}
              onChange={(e) => setFilterStatus(e.target.value)}
              className="w-full px-4 py-2 border border-gray-300 rounded-md focus:ring-green-500 focus:border-green-600"
            >
              <option value="all">All</option>
              <option value="registered">Registered</option>
              <option value="not-registered">Not Registered</option>
            </select>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Compliance Status
            </label>
            <select
              value={filterCompliance}
              onChange={(e) => setFilterCompliance(e.target.value)}
              className="w-full px-4 py-2 border border-gray-300 rounded-md focus:ring-green-500 focus:border-green-600"
            >
              <option value="all">All</option>
              <option value="complete">Complete (4/4)</option>
              <option value="partial">Partial</option>
              <option value="none">None (0/4)</option>
            </select>
          </div>
        </div>
      </div>

      {/* Report Table */}
      <div className="bg-white shadow-sm ring-1 ring-gray-900/5 rounded-2xl overflow-hidden">
        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-800">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-white uppercase tracking-wider">
                  Email
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-white uppercase tracking-wider">
                  Registration Status
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-white uppercase tracking-wider">
                  Compliance Status
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-white uppercase tracking-wider">
                  FSE Code
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-white uppercase tracking-wider">
                  Franchisee Name
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-white uppercase tracking-wider">
                  Added Date
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-white uppercase tracking-wider">
                  Actions
                </th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {filteredData.length === 0 ? (
                <tr>
                  <td colSpan="7" className="px-6 py-4 text-center text-gray-500">
                    No data matches the selected filters.
                  </td>
                </tr>
              ) : (
                filteredData.map((item, index) => (
                  <tr key={index} className="hover:bg-gray-50">
                    <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                      {item.email}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm">
                      {getStatusBadge(item.registrationStatus)}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm">
                      {getComplianceBadge(item.complianceStatus)}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                      {item.fseCode || '-'}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                      {item.franchiseeName || '-'}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                      {item.addedDate ? new Date(item.addedDate).toLocaleDateString() : '-'}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm">
                      {item.registrationStatus === 'registered' && item.complianceStatus?.hasAny ? (
                        <button
                          onClick={() => setSelectedFranchisee(item)}
                          className="inline-flex items-center px-3 py-1 bg-green-600 text-white rounded-full hover:bg-green-500 transition duration-200"
                        >
                          <FaEye className="mr-1" />
                          View Details
                        </button>
                      ) : (
                        <span className="text-gray-400 text-xs">N/A</span>
                      )}
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </div>

      {/* Total Results */}
      <div className="mt-4 text-sm text-gray-600">
        Showing {filteredData.length} of {stats.total} total records
      </div>

      {/* Compliance Details Modal */}
      {selectedFranchisee && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-2xl shadow-xl max-w-2xl w-full max-h-[90vh] overflow-y-auto">
            {/* Modal Header */}
            <div className="sticky top-0 bg-white border-b border-gray-200 px-6 py-4 flex justify-between items-center rounded-t-2xl">
              <h3 className="text-xl font-bold text-gray-900">
                Compliance Details
              </h3>
              <button
                onClick={() => setSelectedFranchisee(null)}
                className="text-gray-400 hover:text-gray-600 transition"
              >
                <FaTimes className="text-2xl" />
              </button>
            </div>

            {/* Modal Body */}
            <div className="px-6 py-4 space-y-6">
              {/* Franchisee Info */}
              <div className="bg-gray-50 rounded-lg p-4">
                <h4 className="font-semibold text-gray-800 mb-3">Franchisee Information</h4>
                <div className="grid grid-cols-2 gap-3 text-sm">
                  <div>
                    <span className="text-gray-600">Email:</span>
                    <p className="font-medium text-gray-900">{selectedFranchisee.email}</p>
                  </div>
                  <div>
                    <span className="text-gray-600">FSE Code:</span>
                    <p className="font-medium text-gray-900">{selectedFranchisee.fseCode || 'N/A'}</p>
                  </div>
                  <div className="col-span-2">
                    <span className="text-gray-600">Franchisee Name:</span>
                    <p className="font-medium text-gray-900">{selectedFranchisee.franchiseeName || 'N/A'}</p>
                  </div>
                </div>
              </div>

              {/* Overall Status */}
              <div className="bg-blue-50 rounded-lg p-4">
                <h4 className="font-semibold text-gray-800 mb-2">Overall Compliance Status</h4>
                <div className="flex items-center justify-between">
                  <span className="text-2xl font-bold text-blue-600">
                    {selectedFranchisee.complianceStatus.completedCount} / {selectedFranchisee.complianceStatus.totalRequired}
                  </span>
                  <div>
                    {selectedFranchisee.complianceStatus.isComplete ? (
                      <span className="text-green-600 font-semibold flex items-center">
                        <FaCheckCircle className="mr-2" /> Complete
                      </span>
                    ) : (
                      <span className="text-yellow-600 font-semibold flex items-center">
                        <FaExclamationTriangle className="mr-2" /> Partial
                      </span>
                    )}
                  </div>
                </div>
              </div>

              {/* Compliance Items */}
              <div>
                <h4 className="font-semibold text-gray-800 mb-3">Compliance Items</h4>
                <div className="space-y-3">
                  
                  {/* Police Certificate */}
                  <div className="border rounded-lg p-4">
                    <div className="flex items-start justify-between">
                      <div className="flex-1">
                        <div className="flex items-center mb-2">
                          <h5 className="font-medium text-gray-900">1. Police Check Certificate</h5>
                          {selectedFranchisee.complianceStatus.details.policeCertificate ? (
                            <FaCheckCircle className="ml-2 text-green-500" />
                          ) : (
                            <FaTimesCircle className="ml-2 text-red-500" />
                          )}
                        </div>
                        {selectedFranchisee.complianceStatus.details.policeCertificate ? (
                          <div className="text-sm space-y-1">
                            {selectedFranchisee.complianceStatus.compliance?.policeCertificate && (
                              <a
                                href={selectedFranchisee.complianceStatus.compliance.policeCertificate}
                                target="_blank"
                                rel="noopener noreferrer"
                                className="text-blue-600 hover:underline flex items-center"
                              >
                                <FaFileAlt className="mr-1" /> View Document
                              </a>
                            )}
                            {selectedFranchisee.complianceStatus.compliance?.policeCertificateExpiryDate && (
                              <p className="text-gray-600">
                                Expires: {new Date(selectedFranchisee.complianceStatus.compliance.policeCertificateExpiryDate).toLocaleDateString()}
                              </p>
                            )}
                          </div>
                        ) : (
                          <p className="text-sm text-red-600">Not uploaded</p>
                        )}
                      </div>
                    </div>
                  </div>

                  {/* Certificate of Currency */}
                  <div className="border rounded-lg p-4">
                    <div className="flex items-start justify-between">
                      <div className="flex-1">
                        <div className="flex items-center mb-2">
                          <h5 className="font-medium text-gray-900">2. Certificate of Currency</h5>
                          {selectedFranchisee.complianceStatus.details.certificateOfCurrency ? (
                            <FaCheckCircle className="ml-2 text-green-500" />
                          ) : (
                            <FaTimesCircle className="ml-2 text-red-500" />
                          )}
                        </div>
                        {selectedFranchisee.complianceStatus.details.certificateOfCurrency ? (
                          <div className="text-sm space-y-1">
                            {selectedFranchisee.complianceStatus.compliance?.certificateOfCurrency && (
                              <a
                                href={selectedFranchisee.complianceStatus.compliance.certificateOfCurrency}
                                target="_blank"
                                rel="noopener noreferrer"
                                className="text-blue-600 hover:underline flex items-center"
                              >
                                <FaFileAlt className="mr-1" /> View Document
                              </a>
                            )}
                            {selectedFranchisee.complianceStatus.compliance?.certificateOfCurrencyStartDate && 
                             selectedFranchisee.complianceStatus.compliance?.certificateOfCurrencyFinishDate && (
                              <p className="text-gray-600">
                                Period: {new Date(selectedFranchisee.complianceStatus.compliance.certificateOfCurrencyStartDate).toLocaleDateString()} - {new Date(selectedFranchisee.complianceStatus.compliance.certificateOfCurrencyFinishDate).toLocaleDateString()}
                              </p>
                            )}
                          </div>
                        ) : (
                          <p className="text-sm text-red-600">Not uploaded</p>
                        )}
                      </div>
                    </div>
                  </div>

                  {/* Influenza Vaccine */}
                  <div className="border rounded-lg p-4">
                    <div className="flex items-start justify-between">
                      <div className="flex-1">
                        <div className="flex items-center mb-2">
                          <h5 className="font-medium text-gray-900">3. Influenza Vaccine Declaration</h5>
                          {selectedFranchisee.complianceStatus.details.influenzaVaccine ? (
                            <FaCheckCircle className="ml-2 text-green-500" />
                          ) : (
                            <FaTimesCircle className="ml-2 text-red-500" />
                          )}
                        </div>
                        {selectedFranchisee.complianceStatus.details.influenzaVaccine ? (
                          <div className="text-sm space-y-1">
                            <p className="text-green-600 font-medium">✓ Declared</p>
                            {selectedFranchisee.complianceStatus.compliance?.influenzaVaccineDate && (
                              <p className="text-gray-600">
                                Vaccination Date: {new Date(selectedFranchisee.complianceStatus.compliance.influenzaVaccineDate).toLocaleDateString()}
                              </p>
                            )}
                          </div>
                        ) : (
                          <p className="text-sm text-red-600">Not declared</p>
                        )}
                      </div>
                    </div>
                  </div>

                  {/* LCHS Fact Sheet */}
                  <div className="border rounded-lg p-4">
                    <div className="flex items-start justify-between">
                      <div className="flex-1">
                        <div className="flex items-center mb-2">
                          <h5 className="font-medium text-gray-900">4. Aged Care Provider Training</h5>
                          {selectedFranchisee.complianceStatus.details.lchsFactSheet ? (
                            <FaCheckCircle className="ml-2 text-green-500" />
                          ) : (
                            <FaTimesCircle className="ml-2 text-red-500" />
                          )}
                        </div>
                        {selectedFranchisee.complianceStatus.details.lchsFactSheet ? (
                          <p className="text-sm text-green-600 font-medium">✓ Acknowledged</p>
                        ) : (
                          <p className="text-sm text-red-600">Not acknowledged</p>
                        )}
                      </div>
                    </div>
                  </div>

                </div>
              </div>
            </div>

            {/* Modal Footer */}
            <div className="sticky bottom-0 bg-gray-50 border-t border-gray-200 px-6 py-4">
              <button
                onClick={() => setSelectedFranchisee(null)}
                className="w-full px-4 py-2 bg-gray-800 text-white rounded-full hover:bg-gray-700 transition duration-200"
              >
                Close
              </button>
            </div>
          </div>
        </div>
      )}
            </>
          )}
        </>
      )}

      {/* Delimiter Selection Modal */}
      {showDelimiterModal && (
        <div
          className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50"
          onClick={() => setShowDelimiterModal(false)}
        >
          <div
            className="bg-white rounded-2xl shadow-2xl p-6 w-96 mx-4"
            onClick={(e) => e.stopPropagation()}
          >
            {/* Header */}
            <div className="flex items-start justify-between mb-1">
              <div>
                <h3 className="text-base font-semibold text-gray-900">Copy Emails</h3>
                <p className="text-xs text-gray-400 mt-0.5">Choose how addresses will be separated</p>
              </div>
              <button
                onClick={() => setShowDelimiterModal(false)}
                className="text-gray-300 hover:text-gray-500 transition ml-4 mt-0.5 flex-shrink-0"
                aria-label="Close"
              >
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>

            {/* Option Cards */}
            <div className="grid grid-cols-2 gap-3 mt-4">
              {[
                { delimiter: ', ', symbol: ',', label: 'Comma', preview: 'jane@email.com,\nalex@email.com' },
                { delimiter: '; ', symbol: ';', label: 'Semicolon', preview: 'jane@email.com;\nalex@email.com' },
              ].map(({ delimiter, symbol, label, preview }) => (
                <button
                  key={label}
                  onClick={() => copyWithDelimiter(delimiter)}
                  className="group relative flex flex-col items-center text-center p-4 rounded-xl border-2 border-gray-100 hover:border-green-600 hover:bg-green-50 transition-all duration-150 cursor-pointer focus:outline-none focus:border-green-600"
                >
                  <span className="text-4xl font-mono font-bold text-gray-300 group-hover:text-green-600 transition-colors duration-150 leading-none mb-2">
                    {symbol}
                  </span>
                  <span className="text-sm font-medium text-gray-700 group-hover:text-green-600 transition-colors duration-150">
                    {label}
                  </span>
                  <span className="mt-2 text-xs text-gray-300 group-hover:text-green-500 font-mono leading-relaxed transition-colors duration-150 whitespace-pre">
                    {preview}
                  </span>
                </button>
              ))}
            </div>

            {/* Cancel */}
            <div className="mt-4 text-center">
              <button
                onClick={() => setShowDelimiterModal(false)}
                className="text-xs text-gray-400 hover:text-gray-600 transition"
              >
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default ReportsPage;
